import { defineStore } from 'pinia';
import { useUI } from './ui';
import { methods } from '@/mixin';
import User from './entity/User';

import {
    API_ROUT_USER,
    API_ROUT_USER_CREATE,
    API_ROUT_USER_ACCESS,
    API_ROUT_USER_RESET,
    API_ROUT_USER_LOGOUT,
    API_ROUT_USER_LOGIN,
    API_ROUT_USER_ISLOGIN,
} from '@/constants';

import { INodeUserAccess, IUserCreate, IUserFromBack, IUsersState } from '@/types';

const usersState: IUsersState = {
    users: [],
    access: [],
    role: [
        {
            text: 'Без доступу',
            short: 'Б',
            key: 'foreign'
        },
        {
            text: 'Редактор',
            short: 'Р',
            key: 'editor'
        },
        {
            text: 'Адміністратор',
            short: 'А',
            key: 'admin'
        },
    ],
};

export const useUsers = defineStore('users', {

    state: () => (usersState),

    getters: {

        all(): User[]
        {
            return this.users;
        },

        allEditors(): User[]
        {
            return this.users.filter(u => u.role === 'editor');
        },

        allWithoutForeign(): User[]
        {
            return this.users.filter(u => u.role != 'foreign');
        },

        current(): User | undefined
        {
            return this.users.find(u => u.current);
        },

        editorAccess(): boolean
        {
            const current = this.current;
            if (current) {
                return ['admin', 'editor'].includes(current.role);
            } else return false;
        },
    },

    actions: {

        one(id: number) {
            return this.users.find(u => u.id == id);
        },

        getAccess(id?: number) {
            if (!id) id = this.current?.id ?? -1;
            return this.access.filter(item => this.current && item.user_id === id);
        },

        isAdmin(id?: number): boolean
        {
            if (!id) id = this.current?.id ?? -1;
            const user = this.one(id);

            if (user) return ['admin'].includes(user.role);
            else return false;
        },

        async fetch() {
            const users = await methods.fetch(API_ROUT_USER).get();
            if (Array.isArray(users)) {
                this.users = users.map((user: IUserFromBack) => {
                    return new User(user);
                });
            }
            this.fetchAccess();
            return users;
        },

        async fetchAccess() {
            const nodeUserAccess = await methods.fetch(API_ROUT_USER_ACCESS).get();
            if (Array.isArray(nodeUserAccess)) {
                this.access = nodeUserAccess.map((item: INodeUserAccess) => {
                    return {user_id: item.user_id, node_id: item.node_id};
                });
            }
            return nodeUserAccess;
        },

        async create(user: IUserCreate) {
            if (!user) return;
            const responseUp = await methods.fetch(API_ROUT_USER_CREATE).post(user, true);
            return responseUp;
        },

        async update(user: User) {
            if (!user) return;
            const response = await methods.fetch(`/user/${user.id}`).patch(user);
            if (response) {
                useUI().setNotification('success', `Дані користувача "${user.name}" успішно оновлено`, 'update-user-success');
            }
            return response;
        },

        async delete(userId: number) {
            if (typeof userId !== 'number') return;
            const response = await methods.fetch(`/user/${userId}`).delete();
            if (response) {
                useUI().setNotification('success', `Користувача успішно видалено`, 'delete-user-success');
            }
            return response;
        },

        async addAccess(user_id: number, node_id: number) {
            return await methods.fetch(API_ROUT_USER_ACCESS).post({user_id, node_id});
        },

        async deleteAccess(user_id: number, node_id: number) {
            return await methods.fetch(API_ROUT_USER_ACCESS).delete({user_id, node_id});
        },

        async resetPass(user: User, newPass: string) {
            const response = await methods.fetch(API_ROUT_USER_RESET + '/' + user.id).patch(newPass);
            if (response) {
                useUI().setNotification('success', `Пароль користувача "${user.name}" успішно змінено`, 'reset-pass-success');
            }
        },

        async logout(user: User) {
            return await methods.fetch(API_ROUT_USER_LOGOUT + '/' + user.id).patch();
        },

        async login(email: string, password: string) {
            const response = await methods.fetch(API_ROUT_USER_LOGIN).post({
                email: email,
                password: password
            }, true);
            return response;
        },

        async islogin() {
            const response = await methods.fetch(API_ROUT_USER_ISLOGIN).get();
            if (response) {
                this.fetch();
                return response;
            } else {
                localStorage.clear();
                document.location.reload();
            }
        },

        push(value: IUserFromBack) {
            if (!this.users.some(u => u.id == value.id)) this.users.push(new User(value));
        },

        replace(value: IUserFromBack) {
            const index = this.users.findIndex(u => u.id == value.id);

            if (index !== -1) {
                const newUser: User = new User(value);
                this.users[index] = newUser;
            }
        },

        splice(userId: number) {
            const index = this.users.findIndex(u => u.id == userId)
            if (index !== -1) this.users.splice(index, 1)
        },

    },
});
